import {
	type ButtonHTMLAttributes,
	createContext,
	type CSSProperties,
	type MouseEvent as ReactMouseEvent,
	useContext
} from 'react'

const label = 'Resize column'

import { persistentAtom } from '@nanostores/persistent'
import clsx from 'clsx'
import { CONSTS } from 'config/objectConst'
import { atom, computed, type WritableAtom } from 'nanostores'

import css from './ContactsTable.module.css'

export const $isRecalculated = atom(false)
export type SizeMode = keyof typeof sizeUtils

const remSize = Number.parseFloat(
	getComputedStyle(document.documentElement).fontSize
)

const INITIAL_SIZES: Record<string, string> = {
	'cell-size': '1fr',
	'wide-cell-size': '2fr'
}

export const SizeKeyContext = createContext<SizeMode>('Contact')

function initSizeStore(key: string) {
	const $sizes = persistentAtom(key, INITIAL_SIZES, {
		decode: JSON.parse,
		encode: JSON.stringify
	})

	const $style = computed($sizes, transformToCustomProperties)

	return {
		$sizes,
		$style,
		handleResize: initEventHandlers($sizes),
		key
	}
}

function transformToCustomProperties(
	sizes: Record<string, string>
): CSSProperties {
	const rules: typeof sizes = {}

	for (const key in sizes) {
		rules[`--${key}`] = sizes[key]
	}

	return rules as CSSProperties
}

export const sizeUtils = {
	Contact: initSizeStore('sizes:c'),
	Executions: initSizeStore('sizes:e')
}

export function ResizeButton({
	className,
	...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
	const resizeKey = useContext(SizeKeyContext)
	const { handleResize } = sizeUtils[resizeKey]

	return (
		<button
			aria-label={label}
			className={clsx(css.resize, className)}
			disabled={!CONSTS.IS_RESIZABLE_TABLE}
			type='button'
			{...props}
			onMouseDown={handleResize}
		/>
	)
}

function initEventHandlers($sizes: WritableAtom<Record<string, string>>) {
	const $isFluidTable = computed($sizes, sizes => sizes['cell-size'] === '1fr')

	let clientX = 0,
		property = '',
		requestId = 0,
		width = 0

	function updateSize(newX: number) {
		const delta = newX - clientX
		clientX = newX

		width += delta
		width = Math.max(width, 120)
		if (property) {
			const prevValues = $sizes.get()
			$sizes.set({
				...prevValues,

				[property]: `${width / remSize}rem`
			})
		}
	}

	function updateSizeWithCancel(event: MouseEvent) {
		requestId && cancelAnimationFrame(requestId)
		requestId = requestAnimationFrame(() => updateSize(event.clientX))
	}

	function useSubscribe() {
		$isRecalculated.set(false)
		document.removeEventListener('mousemove', updateSizeWithCancel)
		document.removeEventListener('mouseup', useSubscribe)
	}

	return function handleResize(event: ReactMouseEvent<HTMLButtonElement>) {
		const button = event.currentTarget

		property = button.value

		const activeCell = button.closest('th')

		width = activeCell?.getBoundingClientRect().width || 0
		clientX = event.clientX

		$isRecalculated.set(true)

		if ($isFluidTable.get()) {
			makeSizesFixed($sizes)
		}
		document.addEventListener('mousemove', updateSizeWithCancel)
		document.addEventListener('mouseup', useSubscribe)
	}
}

function makeSizesFixed($sizes: WritableAtom<Record<string, string>>) {
	// will find contact cell which always rendered and fixed cells `ColumnSettings`
	const referenceCell = document.querySelector<HTMLTableCellElement>(
		`.${css.headCell}`
	)

	if (referenceCell) {
		const wideSize = referenceCell.offsetWidth / remSize
		const prevValues = $sizes.get()
		$sizes.set({
			...prevValues,
			'cell-size': `${wideSize / 2}rem`,
			'wide-cell-size': `${wideSize}rem`
		})
	}
}
